import assert from 'assert';
import type { DataState } from './data/state';
import type {
  ApiReturnDeviceMeasureType,
  ApiReturnAllSensor,
  ApiReturnMeasureType,
  ApiProjectSitesType,
  ApiReturnProject,
  ApiReturnDeviceSite,
  ApiReturnSite,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnDeviceType,
} from '@highimpact-frontend/api-types';

export function getManyToManyEntityIds<
  EntityType extends { id: number },
  RelationType
>(
  entity: EntityType,

  relations: DataState<string, { [KEY in number]: RelationType }>,

  entity_key: keyof RelationType,
  mapped_key: keyof RelationType
) {
  return Object.values(relations.data)
    .filter((relation) => relation[entity_key] === entity.id)
    .map((relation) => relation[mapped_key] as number);
}

export function getManyToOneEntity<
  EntityType extends { id: number },
  MappingType extends { id: number },
  RelationType
>(
  keyEntity: EntityType,
  mappingEntity: MappingType,
  relations: DataState<string, { [KEY in number]: RelationType }>,
  entityKey: keyof RelationType,
  mappedKey: keyof RelationType
): RelationType | undefined {
  return Object.values(relations.data).find(
    (relation) =>
      relation[entityKey] === keyEntity.id &&
      relation[mappedKey] === mappingEntity.id
  );
}

export function getManyToManyEntities<
  RelationType,
  EntityType extends { id: number },
  MappingType
>(
  entity: EntityType,

  relations: DataState<string, { [KEY in number]: RelationType }>,
  mapping_table: DataState<string, { [KEY in number]: MappingType }>,

  entity_key: keyof RelationType,
  mapped_key: keyof RelationType
) {
  const entity_ids = getManyToManyEntityIds(
    entity,
    relations,
    entity_key,
    mapped_key
  );

  return entity_ids
    .filter((entity_id) => mapping_table.data[entity_id])
    .map((entity_id) => {
      assert(mapping_table.data[entity_id]);

      return mapping_table.data[entity_id];
    });
}

export const getSitesFromProject = (
  project: ApiReturnProject,

  project_sites: DataState<string, { [KEY in number]: ApiProjectSitesType }>,
  sites: DataState<string, { [KEY in number]: ApiReturnSite }>
) =>
  getManyToManyEntities(project, project_sites, sites, 'project_id', 'site_id');

export const getProjectFromSites = (
  site: ApiReturnSite,

  project_sites: DataState<string, { [KEY in number]: ApiProjectSitesType }>,
  projects: DataState<string, { [KEY in number]: ApiReturnProject }>
) =>
  getManyToManyEntities(site, project_sites, projects, 'site_id', 'project_id');

export const getDevicesFromSite = (
  site: ApiReturnSite,

  site_devices: DataState<string, { [KEY in number]: ApiReturnDeviceSite }>,
  devices: DataState<string, { [KEY in number]: ApiReturnAllSensor }>
) => getManyToManyEntities(site, site_devices, devices, 'site_id', 'device_id');

export const getMeasureTypesFromDevice = (
  device: ApiReturnAllSensor,

  device_measure_types: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceMeasureType }
  >,
  measure_types: DataState<string, { [KEY in number]: ApiReturnMeasureType }>
) =>
  getManyToManyEntities(
    device,
    device_measure_types,
    measure_types,
    'device_id',
    'measure_type_id'
  );

export const getMeasureTypesFromDeviceType = (
  device_type: ApiReturnDeviceType,

  device_type_measure_types: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >,
  measure_types: DataState<string, { [KEY in number]: ApiReturnMeasureType }>
) =>
  getManyToManyEntities(
    device_type,
    device_type_measure_types,
    measure_types,
    'device_type_id',
    'measure_type_id'
  );

export const getDeviceTypesFromMeasureType = (
  measure_type: ApiReturnMeasureType,

  device_type_measure_types: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >,
  device_types: DataState<string, { [KEY in number]: ApiReturnDeviceType }>
) =>
  getManyToManyEntities(
    measure_type,
    device_type_measure_types,
    device_types,
    'measure_type_id',
    'device_type_id'
  );

export const getDevicesFromMeasureType = (
  measure_type: ApiReturnMeasureType,
  device_types_measure_types: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >,
  devices: DataState<string, { [KEY in number]: ApiReturnAllSensor }>,
  device_types: DataState<string, { [KEY in number]: ApiReturnDeviceType }>
) => {

  const relatedDeviceTypes = getDeviceTypesFromMeasureType(
    measure_type,
    device_types_measure_types,
    device_types
  );

  return Object.values(devices.data).filter(device =>
    relatedDeviceTypes.some(dt => dt.id === device.device_type_id)
  );
};



export const getSiteIdsFromDevice = (
  device: ApiReturnAllSensor,

  site_devices: DataState<string, { [KEY in number]: ApiReturnDeviceSite }>
) => getManyToManyEntityIds(device, site_devices, 'device_id', 'site_id');

export const getSitesFromDevice = (
  device: ApiReturnAllSensor,
  site_devices: DataState<string, { [KEY in number]: ApiReturnDeviceSite }>,
  sites: DataState<string, { [KEY in number]: ApiReturnSite }>
) => getManyToManyEntities(device, site_devices, sites, 'device_id', 'site_id');

export const getDeviceSiteFromSiteAndDevice = (
  device: ApiReturnAllSensor,
  site: ApiReturnSite,
  siteDevices: DataState<string, { [KEY in number]: ApiReturnDeviceSite }>
): ApiReturnDeviceSite | undefined => {
  return getManyToOneEntity(device, site, siteDevices, 'device_id', 'site_id');
};

export const getNonOrphanedProjects = (
  projects: DataState<string, { [KEY in number]: ApiReturnProject }>,
  project_sites: DataState<string, { [KEY in number]: ApiProjectSitesType }>
): ApiReturnProject[] => {
  const uniqueProjectIds = new Set(
    Object.values(project_sites.data)
      .map(({ project_id }) => project_id)
      .filter((id) => id !== undefined && id !== null)
  );
  return Array.from(uniqueProjectIds)
    .map((id) => projects.data[id])
    .filter((project) => project !== undefined);
};
